/* eslint-disable react-refresh/only-export-components */
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import {
  AuthSession,
  clearSession,
  getValidAccessToken,
  loadStoredSession,
  loginWithPassword,
  parseCallbackHashSession,
  requestPasswordReset,
  saveSession,
  signUpWithPassword,
  startPasswordlessEmail,
  verifyPasswordlessOtp,
} from './auth0Api';
import { AuthContext, AuthContextValue, LogoutOptions } from './authContext';

export type { LogoutOptions } from './authContext';
export { isAuthConfigured } from './auth0Api';
export { useAuth, useAuth0 } from './useAuth0';

type AuthProviderProps = {
  children?: ReactNode;
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return parseCallbackHashSession() ?? loadStoredSession();
  });

  useEffect(() => {
    // Re-check hash on mount for passwordless redirects that land after paint.
    const fromHash = parseCallbackHashSession();
    if (fromHash) {
      setSession(fromHash);
    }
  }, []);

  const applySession = useCallback((next: AuthSession) => {
    saveSession(next);
    setSession(next);
  }, []);

  const handleLoginWithPassword = useCallback(
    async (email: string, password: string) => {
      const next = await loginWithPassword(email, password);
      applySession(next);
    },
    [applySession],
  );

  const handleSignUpWithPassword = useCallback(
    async (email: string, password: string, fullName: string) => {
      const next = await signUpWithPassword(email, password, fullName);
      applySession(next);
    },
    [applySession],
  );

  const handleRequestPasswordReset = useCallback(async (email: string) => {
    await requestPasswordReset(email);
  }, []);

  const handleStartPasswordless = useCallback(
    async (email: string, send: 'code' | 'link' = 'code') => {
      await startPasswordlessEmail(email, send);
    },
    [],
  );

  const handleVerifyPasswordless = useCallback(
    async (email: string, otp: string) => {
      const next = await verifyPasswordlessOtp(email, otp);
      applySession(next);
    },
    [applySession],
  );

  const getAccessTokenSilently = useCallback(async () => {
    return getValidAccessToken();
  }, []);

  const logout = useCallback((options?: LogoutOptions) => {
    clearSession();
    setSession(null);
    const returnTo =
      options?.logoutParams?.returnTo ||
      options?.returnTo ||
      window.location.origin;
    window.location.assign(returnTo);
  }, []);

  const loginWithRedirect = useCallback(() => {
    window.location.assign('/login');
  }, []);

  const loginWithPopup = useCallback(async () => {
    window.location.assign('/login');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(session?.accessToken),
      isLoading: false,
      user: session?.user,
      loginWithPassword: handleLoginWithPassword,
      signUpWithPassword: handleSignUpWithPassword,
      requestPasswordReset: handleRequestPasswordReset,
      startPasswordless: handleStartPasswordless,
      verifyPasswordless: handleVerifyPasswordless,
      getAccessTokenSilently,
      logout,
      loginWithRedirect,
      loginWithPopup,
    }),
    [
      session,
      handleLoginWithPassword,
      handleSignUpWithPassword,
      handleRequestPasswordReset,
      handleStartPasswordless,
      handleVerifyPasswordless,
      getAccessTokenSilently,
      logout,
      loginWithRedirect,
      loginWithPopup,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children ?? <Outlet />}
    </AuthContext.Provider>
  );
};
